import { MultiLineChart } from '@toggle/design-system';
import { addDays, isFriday, isSameMinute } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { isTimeBetweenMidnightAnd930 } from '~/components/price-display-view/helpers/price-display-helpers';
import { usePriceChangeHistory } from '~/hooks/use-price-change-history/usePriceHistory';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import * as S from './MarketReaction.styles';

export interface MarketReactionProps {
  reportingDate: string;
  subscribableTicker: string;
}

export const MarketReaction = ({
  reportingDate,
  subscribableTicker,
}: MarketReactionProps) => {
  const { t } = useTranslation(['earnings']);
  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.bullish.value,
    negative: colors.threshold.bearish.value,
  };

  const reportingDateET = toZonedTime(reportingDate, 'America/New_York');
  const isReportingFriday = isFriday(reportingDateET);
  const isReportingPreMarket = isTimeBetweenMidnightAnd930(reportingDateET);

  const fromDate = new Date(
    reportingDateET.getUTCFullYear(),
    reportingDateET.getUTCMonth(),
    reportingDateET.getUTCDate(),
    isReportingPreMarket ? 4 : 9,
    isReportingPreMarket ? 0 : 30,
    0,
    0
  );

  const fromDateUTC = fromZonedTime(fromDate, 'America/New_York');

  const toDate = addDays(
    new Date(
      reportingDateET.getUTCFullYear(),
      reportingDateET.getUTCMonth(),
      reportingDateET.getUTCDate(),
      16,
      0,
      0,
      0
    ),
    isReportingFriday ? 3 : 1
  );

  const toDateUTC = fromZonedTime(toDate, 'America/New_York');

  const { data } = usePriceChangeHistory({
    ticker: subscribableTicker,
    reference: fromDateUTC.toISOString(),
    from: fromDateUTC.toISOString(),
    to: toDateUTC.toISOString(),
  });

  if (!data?.length) {
    return null;
  }

  const tsData =
    data?.map(item => ({
      date: toZonedTime(new Date(item.time), 'America/New_York'),
      value: item.value,
    })) ?? [];

  const line = {
    color: 'var(--icon-default)',
    data: tsData,
    label: 'Market open/close',
  };

  const range =
    Math.max(...tsData.map(item => item.value)) -
    Math.min(...tsData.map(item => item.value));
  const yAxisFormatType = range > 4 ? 'percent' : 'percent1f';

  const getVerticalLines = () => {
    const verticalLines = [
      {
        x: new Date(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate(),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: new Date(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate(),
          16,
          0,
          0,
          0
        ),
      },
      {
        x: new Date(
          toDate.getUTCFullYear(),
          toDate.getUTCMonth(),
          toDate.getUTCDate(),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: new Date(
          toDate.getUTCFullYear(),
          toDate.getUTCMonth(),
          toDate.getUTCDate(),
          16,
          0,
          0,
          0
        ),
      },
    ];

    return verticalLines.filter(line =>
      tsData.some(item => isSameMinute(item.date, line.x))
    );
  };

  return (
    <S.Container>
      <S.Legend>
        <S.LegendItemVector />
        <S.LegendItemLabel>
          {t('earnings:earningsInsight.drawer.earningsRecap.marketOpenClose')}
        </S.LegendItemLabel>
      </S.Legend>
      {line.data.length > 0 && (
        <MultiLineChart
          lines={[line]}
          fillColors={fillColors}
          yAxisFormatType={yAxisFormatType}
          thresholdData={tsData}
          isMarketReaction
          verticalLines={getVerticalLines()}
        />
      )}
    </S.Container>
  );
};
